import React, { useState } from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    CardActions,
    Avatar,
    Typography,
    Divider,
    Button,
    Modal,
    Box,
    CircularProgress,
    Alert,
    IconButton,
    Chip,
} from '@mui/material';
import { deepPurple } from '@mui/material/colors';
import CloseIcon from '@mui/icons-material/Close';
import { fetchPromptById } from '../api/promptApi';
import { fetchCopyByPromptId, fetchCopyById } from '../api/copyApi'; // Import API function for fetching copy
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { formatDate } from '../utils/dateUtils';

const PromptCard = ({ id, title, creationDate, user, userName, handleDelete }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [details, setDetails] = useState(null);
    const [generatedCopy, setGeneratedCopy] = useState([]); // State for generated copy
    const [selectedCopy, setSelectedCopy] = useState(null); // State for selected copy to view
    const [copyDetails, setCopyDetails] = useState(null); // State for copy details
    const [copyLoading, setCopyLoading] = useState(false); // State for copy loading
    const [copyError, setCopyError] = useState(null); // State for copy error

    const handleOpen = async () => {
        setOpen(true);
        setLoading(true);
        setError(null);
        try {
            const [promptDetails, copyData] = await Promise.all([
                fetchPromptById(id),
                fetchCopyByPromptId(id),
            ]);

            console.log('Prompt id:', id);
            console.log('Fetched prompt details:', promptDetails);
            console.log('Fetched copy data:', copyData);

            if (promptDetails && promptDetails.length > 0) {
                setDetails(promptDetails[0]);
            } else {
                setError('No prompt details found');
            }
            setGeneratedCopy(copyData || []); // Set the copy data if returned
        } catch (err) {
            console.error(err);
            setError('Failed to fetch details');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setDetails(null);
        setGeneratedCopy([]);
    };

    const handleCopyOpen = async (copy) => {
        setSelectedCopy(copy);
        setCopyLoading(true);
        setCopyError(null);
        try {
            const response = await fetchCopyById(copy.id);
            if (Array.isArray(response) && response.length > 0) {
                setCopyDetails(response[0]);
            } else {
                throw new Error('Unexpected response format');
            }
        } catch (err) {
            console.error('Error fetching copy details:', err);
            setCopyError('Failed to fetch details');
        } finally {
            setCopyLoading(false);
        }
    };

    const handleCopyClose = () => {
        setSelectedCopy(null);
        setCopyDetails(null);
    };

    const handleCopyToClipboard = () => {
        if (copyDetails && copyDetails.copyText) {
            navigator.clipboard.writeText(copyDetails.copyText).then(
                () => {
                    alert('Text copied to clipboard!');
                },
                (err) => {
                    console.error('Failed to copy text:', err);
                }
            );
        }
    };

    return (
        <>
            <Card
                sx={{
                    maxWidth: 345,
                    margin: 'auto',
                    transition: '0.3s',
                    boxShadow: '0 8px 40px -12px rgba(0,0,0,0.3)',
                    '&:hover': {
                        boxShadow: '0 16px 70px -12.125px rgba(0,0,0,0.3)',
                    },
                }}
            >
                <CardHeader
                    avatar={
                        <Avatar sx={{ bgcolor: deepPurple[500] }}>
                            {user.charAt(0).toUpperCase()}
                        </Avatar>
                    }
                    subheader={`User: ${userName}`}
                />
                <Divider variant="middle" />
                <CardContent>
                    <Typography variant="body2" color="textSecondary">
                        {title}
                    </Typography>
                    <Divider />
                    <Typography variant="caption" color="textSecondary">
                        Created on: {formatDate(creationDate)}
                    </Typography>
                </CardContent>
                <CardActions>
                    <Button size="small" onClick={handleOpen}>
                        Details
                    </Button>
                    <Button
                        size="small"
                        color="error"
                        onClick={() => handleDelete(id)}
                    >
                        Delete
                    </Button>
                </CardActions>
            </Card>

            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="prompt-detail-title"
                aria-describedby="prompt-detail-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '80%',
                        maxWidth: 600,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 2,
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Typography id="prompt-detail-title" variant="h6" component="h2">
                            Full Prompt
                        </Typography>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider />
                    
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    ) : details ? (
                        <>
                            <Typography variant="h6" sx={{ mt: 2 }}>
                                System Prompt
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {details.userSystemPrompt || 'No system prompt'}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" sx={{ mt: 2 }}>
                                User Prompt
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {details.userPrompt || 'No user prompt'}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" sx={{ mt: 2 }}>
                                Full Text
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {details.fullPromptText || 'No full text available'}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                Keywords:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {details.keyWords?.map((keyword) => (
                                    <Chip key={keyword.id} label={keyword.keywordText} />
                                ))}
                            </Box>
                            
                            {generatedCopy && generatedCopy.length > 0 && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="h6" sx={{ mt: 2 }}>
                                        Copy Generated From Prompt
                                    </Typography>
                                    {generatedCopy.map((copy) => (
                                        <Box key={copy.id} sx={{ my: 1 }}>
                                            <Typography variant="body2" color="textSecondary">
                                                {copy.copySummary}
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                sx={{ mt: 1 }}
                                                onClick={() => handleCopyOpen(copy)}
                                            >
                                                View
                                            </Button>
                                        </Box>
                                    ))}
                                </>
                            )}
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body2" color="textSecondary">
                                Created on: {formatDate(details.creationDate)}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                By: {details.userName}
                            </Typography>
                        </>
                    ) : null}
                </Box>
            </Modal>
            {selectedCopy && (
                <Modal
                    open={!!selectedCopy}
                    onClose={handleCopyClose}
                    sx={{
                        position: 'absolute',
                        zIndex: 1300, // Higher than the prompt details modal
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '80%',
                            maxWidth: 600,
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 2,
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 2,
                            }}
                        >
                            <Typography variant="h6" component="h2">
                                Copy Detail
                            </Typography>
                            <IconButton onClick={handleCopyClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Divider />
                        {copyLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <CircularProgress />
                            </Box>
                        ) : copyError ? (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {copyError}
                            </Alert>
                        ) : copyDetails ? (
                            <>
                                <Typography
                                    sx={{ mt: 2, whiteSpace: 'pre-wrap' }}
                                >
                                    {copyDetails.copyText || 'No copy text available.'}
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="body2" color="textSecondary">
                                    Summary: {copyDetails.copySummary || 'No summary available.'}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                    Created on: {formatDate(copyDetails.creationDate)}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    By: {copyDetails.userName || 'Unknown'}
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<ContentCopyIcon />}
                                        onClick={handleCopyToClipboard}
                                        disabled={!copyDetails || !copyDetails.copyText}
                                    >
                                        Copy
                                    </Button>
                                    <Button variant="outlined" color="secondary" onClick={handleCopyClose}>
                                        Close
                                    </Button>
                                </Box>
                            </>
                        ) : null}
                    </Box>
                </Modal>
            )}
        </>
    );
};

export default PromptCard;
